import type { UIMessage } from 'ai'
import type { LegislationDocumentByCelexResponse } from '@/shared/hooks/api/radarScopeTier'
import {
  extractCelexFromCitationHref,
  isAnnexCitationLabel,
  parseInternalLegislationViewerUrl,
  parseSearchRadarRagChunksOutput,
  toLegislationCitationTarget,
  toLegislationCitationTargetFromCelexLookup,
  type LegislationCitationTarget,
} from './llmChatToolOutput'

type CitationCandidate = {
  target: LegislationCitationTarget
  rank: number | null
  occurrence: number
  messageId: string
  messageIndex: number
}

export type CitationResolutionIndex = {
  allCandidates: CitationCandidate[]
  candidatesByCelex: Map<string, CitationCandidate[]>
  messageIndexById: Map<string, number>
  messageIdsByIndex: string[]
  assistantMessageIndexes: Set<number>
  candidatesByMessageId: Map<string, CitationCandidate[]>
  messageCount: number
}

function collectCitationCandidates(messages: UIMessage[]): {
  candidates: CitationCandidate[]
  assistantMessageIndexes: Set<number>
  messageIndexById: Map<string, number>
  messageIdsByIndex: string[]
} {
  const candidates: CitationCandidate[] = []
  const assistantMessageIndexes = new Set<number>()
  const messageIndexById = new Map<string, number>()
  const messageIdsByIndex: string[] = []
  let occurrence = 0

  messages.forEach((message, messageIndex) => {
    messageIndexById.set(message.id, messageIndex)
    messageIdsByIndex[messageIndex] = message.id
    if (message.role !== 'assistant') {
      return
    }
    assistantMessageIndexes.add(messageIndex)
    message.parts.forEach((part) => {
      if (
        part.type !== 'dynamic-tool' ||
        part.toolName !== 'search_radar_rag_chunks_hybrid' ||
        part.state !== 'output-available'
      ) {
        return
      }
      const parsed = parseSearchRadarRagChunksOutput(part.output)
      if (!parsed) {
        return
      }
      parsed.items.forEach((item) => {
        const target = toLegislationCitationTarget(item)
        if (!target) {
          return
        }
        candidates.push({
          target,
          rank: item.rank,
          occurrence,
          messageId: message.id,
          messageIndex,
        })
        occurrence += 1
      })
    })
  })

  return {
    candidates,
    assistantMessageIndexes,
    messageIndexById,
    messageIdsByIndex,
  }
}

export function buildCitationResolutionIndex(
  messages: UIMessage[]
): CitationResolutionIndex {
  const {
    candidates,
    assistantMessageIndexes,
    messageIndexById,
    messageIdsByIndex,
  } = collectCitationCandidates(messages)
  const candidatesByCelex = new Map<string, CitationCandidate[]>()
  const candidatesByMessageId = new Map<string, CitationCandidate[]>()
  for (const candidate of candidates) {
    const byCelex = candidatesByCelex.get(candidate.target.celex) ?? []
    byCelex.push(candidate)
    candidatesByCelex.set(candidate.target.celex, byCelex)

    const byMessageId = candidatesByMessageId.get(candidate.messageId) ?? []
    byMessageId.push(candidate)
    candidatesByMessageId.set(candidate.messageId, byMessageId)
  }
  return {
    allCandidates: candidates,
    candidatesByCelex,
    messageIndexById,
    messageIdsByIndex,
    assistantMessageIndexes,
    candidatesByMessageId,
    messageCount: messages.length,
  }
}

function pickBestCitationCandidate(
  candidates: CitationCandidate[]
): CitationCandidate | null {
  if (candidates.length === 0) {
    return null
  }
  const [best] = [...candidates].sort((left, right) => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
    if (leftRank !== rightRank) {
      return leftRank - rightRank
    }
    return right.occurrence - left.occurrence
  })
  return best ?? null
}

function withInternalBlockIds(
  target: LegislationCitationTarget,
  internal: { startBlockId: string; endBlockId: string }
): LegislationCitationTarget {
  return {
    ...target,
    startBlockId: internal.startBlockId,
    endBlockId: internal.endBlockId,
  }
}

export function resolveCitationTargetFromHrefWithIndex(input: {
  href: string
  label: string
  sourceMessageId: string
  index: CitationResolutionIndex
}): LegislationCitationTarget | null {
  const internal = parseInternalLegislationViewerUrl(input.href)
  if (isAnnexCitationLabel(input.label) && !internal) {
    return null
  }
  const celex = internal?.celex ?? extractCelexFromCitationHref(input.href)
  if (!celex) {
    return null
  }

  const matchingByCelex = input.index.candidatesByCelex.get(celex) ?? []
  if (matchingByCelex.length === 0) {
    return null
  }

  const filtered = internal
    ? matchingByCelex.filter(
        (candidate) =>
          candidate.target.startBlockId === internal.startBlockId &&
          candidate.target.endBlockId === internal.endBlockId
      )
    : matchingByCelex

  const targetPool = filtered.length > 0 ? filtered : matchingByCelex
  const sameMessageCandidates = targetPool.filter(
    (candidate) => candidate.messageId === input.sourceMessageId
  )
  const sameMessageBest = pickBestCitationCandidate(sameMessageCandidates)
  if (sameMessageBest) {
    const target = {
      ...sameMessageBest.target,
      articleLabel:
        input.label.trim().length > 0 ? input.label : sameMessageBest.target.articleLabel,
    }
    if (internal) {
      return withInternalBlockIds(target, internal)
    }
    return target
  }

  const sourceIndex = input.index.messageIndexById.get(input.sourceMessageId)
  const startIndex =
    sourceIndex !== undefined ? sourceIndex - 1 : input.index.messageCount - 1

  for (let index = startIndex; index >= 0; index -= 1) {
    if (!input.index.assistantMessageIndexes.has(index)) {
      continue
    }
    const messageId = input.index.messageIdsByIndex[index]
    if (!messageId) {
      continue
    }
    const messageCandidates = targetPool.filter(
      (candidate) => candidate.messageId === messageId
    )
    const best = pickBestCitationCandidate(messageCandidates)
    if (best) {
      const target = {
        ...best.target,
        articleLabel: input.label.trim().length > 0 ? input.label : best.target.articleLabel,
      }
      if (internal) {
        return withInternalBlockIds(target, internal)
      }
      return target
    }
  }

  const best = pickBestCitationCandidate(targetPool)
  if (!best) {
    return null
  }
  const target = {
    ...best.target,
    articleLabel: input.label.trim().length > 0 ? input.label : best.target.articleLabel,
  }
  if (internal) {
    return withInternalBlockIds(target, internal)
  }
  return target
}

export function resolveCitationTargetFromHref(input: {
  href: string
  label: string
  sourceMessageId: string
  messages: UIMessage[]
}): LegislationCitationTarget | null {
  const index = buildCitationResolutionIndex(input.messages)
  return resolveCitationTargetFromHrefWithIndex({
    href: input.href,
    label: input.label,
    sourceMessageId: input.sourceMessageId,
    index,
  })
}

export async function resolveCitationTargetFromHrefOrRadar(input: {
  href: string
  label: string
  sourceMessageId: string
  index: CitationResolutionIndex
  lookupByCelex: (celex: string) => Promise<LegislationDocumentByCelexResponse | null>
}): Promise<LegislationCitationTarget | null> {
  const internal = parseInternalLegislationViewerUrl(input.href)
  const fromToolOutput = resolveCitationTargetFromHrefWithIndex({
    href: input.href,
    label: input.label,
    sourceMessageId: input.sourceMessageId,
    index: input.index,
  })
  if (fromToolOutput) {
    return fromToolOutput
  }

  const celex = internal?.celex ?? extractCelexFromCitationHref(input.href)
  if (!celex) {
    return null
  }

  if (isAnnexCitationLabel(input.label) && !internal) {
    return null
  }

  const documentMeta = await input.lookupByCelex(celex)
  if (!documentMeta) {
    return null
  }

  if (internal) {
    return toLegislationCitationTargetFromCelexLookup({
      celex: documentMeta.celex,
      documentId: documentMeta.documentId,
      startBlockId: internal.startBlockId,
      endBlockId: internal.endBlockId,
      articleLabel: input.label,
    })
  }

  return toLegislationCitationTargetFromCelexLookup({
    celex: documentMeta.celex,
    documentId: documentMeta.documentId,
    startBlockId: documentMeta.startBlockId,
    endBlockId: documentMeta.endBlockId,
    articleLabel: input.label,
  })
}
